import { ReactNode } from 'react';

export interface ResponsiveTableColumn<T = any> {
  key: string;
  label: string | ReactNode;
  render?: (item: T, index: number) => ReactNode;
  mobileHidden?: boolean; // Colonnes à cacher sur mobile
  mobileLabel?: string; // Label différent sur mobile
}

interface ResponsiveTableProps<T = any> {
  columns: ResponsiveTableColumn<T>[];
  data: T[];
  mobileCardTitle?: (item: T) => string | ReactNode;
  mobileCardActions?: (item: T) => ReactNode;
  emptyMessage?: string | ReactNode;
  loading?: boolean;
  loadingMessage?: string;
  className?: string;
  tableClassName?: string;
}

export const ResponsiveTable = <T extends { id?: number | string; session_id?: string }>({
  columns,
  data,
  mobileCardTitle,
  mobileCardActions,
  emptyMessage = 'Aucune donnée disponible',
  loading = false,
  loadingMessage = 'Chargement...',
  className = '',
  tableClassName = 'products-table',
}: ResponsiveTableProps<T>) => {
  // Filtrer les colonnes visibles sur mobile
  const mobileColumns = columns.filter(col => !col.mobileHidden);

  return (
    <div className={`table-container ${className}`}>
      {/* Version Desktop */}
      <div className="table-desktop">
        <table className={tableClassName}>
          <thead>
            <tr>
              {columns.map(col => (
                <th key={col.key}>{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="table-loading">
                  <div>{loadingMessage}</div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="table-empty">
                  <div>
                    {typeof emptyMessage === 'string' ? emptyMessage : emptyMessage}
                  </div>
                </td>
              </tr>
            ) : (
              data.map((item, index) => (
                <tr key={item.id || index}>
                  {columns.map(col => (
                    <td key={col.key}>
                      {col.render ? col.render(item, index) : (item as any)[col.key] || '-'}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Version Mobile */}
      <div className="table-mobile">
        {loading ? (
          <div className="table-loading">
            {loadingMessage}
          </div>
        ) : data.length === 0 ? (
          <div className="table-empty">
            {typeof emptyMessage === 'string' ? emptyMessage : emptyMessage}
          </div>
        ) : (
          data.map((item, index) => (
            <div key={item.id || index} className="table-card-item">
              <div className="table-card-header">
                <h3 className="table-card-title">
                  {mobileCardTitle ? mobileCardTitle(item) : (item as any).name || `Item #${index + 1}`}
                </h3>
                {mobileCardActions && (
                  <div className="table-card-actions">
                    {mobileCardActions(item)}
                  </div>
                )}
              </div>
              <div className="table-card-body">
                {mobileColumns.map(col => {
                  const value = col.render ? col.render(item, index) : (item as any)[col.key];
                  if (value === null || value === undefined || value === '') {
                    return null;
                  }
                  return (
                    <div key={col.key} className="table-card-row">
                      <span className="table-card-label">
                        {col.mobileLabel || col.label}
                      </span>
                      <span className="table-card-value">
                        {typeof value === 'string' || typeof value === 'number' ? (
                          <strong>{value}</strong>
                        ) : (
                          value
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
